import React, { useState, useRef, useCallback, useEffect } from 'react';
import './VariantCompare.css';

const VARIANT_LABELS = ['🔥 Bold', '✨ Minimal', '🌑 Dark'];

export default function VariantCompare({ variants }) {
  const [open, setOpen] = useState(false);
  const [idxA, setIdxA] = useState(0);
  const [idxB, setIdxB] = useState(1);
  const [sliderPct, setSliderPct] = useState(50); // 0–100
  const stageRef = useRef(null);
  const dragging = useRef(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Prevent body scroll when modal open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const getPctFromEvent = useCallback((e) => {
    const stage = stageRef.current;
    if (!stage) return 50;
    const rect = stage.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    return Math.min(100, Math.max(0, (x / rect.width) * 100));
  }, []);

  const onMouseDown = useCallback((e) => {
    dragging.current = true;
    setSliderPct(getPctFromEvent(e));
    e.preventDefault();
  }, [getPctFromEvent]);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    setSliderPct(getPctFromEvent(e));
  }, [getPctFromEvent]);

  const onMouseUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const onTouchStart = useCallback((e) => {
    dragging.current = true;
    setSliderPct(getPctFromEvent(e));
  }, [getPctFromEvent]);

  const onTouchMove = useCallback((e) => {
    if (!dragging.current) return;
    setSliderPct(getPctFromEvent(e));
    e.preventDefault();
  }, [getPctFromEvent]);

  const onTouchEnd = useCallback(() => {
    dragging.current = false;
  }, []);

  // Attach global move/up listeners when dragging
  useEffect(() => {
    if (!open) return;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [open, onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  // Clamp A/B indices so they don't match
  const handleSelectA = (i) => {
    setIdxA(i);
    if (i === idxB) setIdxB(variants.findIndex((_, j) => j !== i));
  };
  const handleSelectB = (i) => {
    setIdxB(i);
    if (i === idxA) setIdxA(variants.findIndex((_, j) => j !== i));
  };

  if (!variants || variants.length < 2) return null;

  const imgA = variants[idxA];
  const imgB = variants[idxB];

  return (
    <>
      <button
        className="variant-compare-trigger"
        onClick={() => { setOpen(true); setSliderPct(50); }}
        title="Compare two variants side by side"
      >
        ⚖️ Compare
      </button>

      {open && (
        <div
          className="compare-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="compare-modal" role="dialog" aria-modal="true" aria-label="Variant comparison">
            <div className="compare-modal-header">
              <span className="compare-modal-title">⚖️ Compare Variants</span>
              <button className="compare-modal-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>

            {/* Variant picker */}
            <div className="compare-picker">
              <span className="compare-picker-label">A:</span>
              <div className="compare-picker-variants">
                {variants.map((url, i) => (
                  <button
                    key={i}
                    className={`compare-picker-thumb ${idxA === i ? 'selected-a' : ''}`}
                    onClick={() => handleSelectA(i)}
                    title={VARIANT_LABELS[i] || `Variant ${i + 1}`}
                  >
                    <img src={url} alt={VARIANT_LABELS[i] || `Variant ${i + 1}`} />
                  </button>
                ))}
              </div>
              <span className="compare-picker-divider">vs</span>
              <span className="compare-picker-label">B:</span>
              <div className="compare-picker-variants">
                {variants.map((url, i) => (
                  <button
                    key={i}
                    className={`compare-picker-thumb ${idxB === i ? 'selected-b' : ''}`}
                    onClick={() => handleSelectB(i)}
                    title={VARIANT_LABELS[i] || `Variant ${i + 1}`}
                  >
                    <img src={url} alt={VARIANT_LABELS[i] || `Variant ${i + 1}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Compare stage */}
            <div
              className="compare-stage"
              ref={stageRef}
              onMouseDown={onMouseDown}
              onTouchStart={onTouchStart}
            >
              {/* Image A (full background) */}
              <div className="compare-img-a">
                <img src={imgA} alt="Variant A" draggable={false} />
              </div>

              {/* Image B (clipped to right of divider) */}
              <div
                className="compare-img-b"
                style={{ clipPath: `inset(0 ${100 - sliderPct}% 0 0)` }}
              >
                <img src={imgB} alt="Variant B" draggable={false} />
              </div>

              {/* Divider line */}
              <div
                className="compare-divider-line"
                style={{ left: `${sliderPct}%` }}
              />

              {/* Divider handle */}
              <div
                className="compare-divider-handle"
                style={{ left: `${sliderPct}%` }}
              >
                ‹›
              </div>

              {/* Labels */}
              <div className="compare-label compare-label-a">
                {VARIANT_LABELS[idxA] || `A`}
              </div>
              <div className="compare-label compare-label-b">
                {VARIANT_LABELS[idxB] || `B`}
              </div>
            </div>

            <p className="compare-drag-hint">
              ← Drag the divider to reveal either variant →
            </p>
          </div>
        </div>
      )}
    </>
  );
}
